import React, { useMemo, useRef } from 'react';
import { IonContent, IonProgressBar } from '@ionic/react';
import { Container, CustomButton, decOfNum } from '@mindly/ui-components';
import PageHeader from '../../components/PageHeader/PageHeader';
import { SubscriptionModalContainer, SubscriptionModalStyled } from './SubscriptionModal.style';
import InfoIcon from './info-icon.svg?react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { lastActiveSubscriptionSelector } from '../../redux/subscriptions/selector';
import useLangs from '../../context/LangsContext';
import { ISubscription } from '../../redux/subscriptions/types';
import HelpButton from '../../components/HelpButton';
import { PrivateRoutes } from '../../routes/navigation';
import { useHistory } from 'react-router';
import { globalVariables } from '../../redux/firestoreGlobalVariables/selector';
import { subscriptions } from '../../redux/subscriptions/reducer';
import { DateTime } from 'luxon';
import { SUBSCRIPTION_NOT_DO_IN_TIME } from '../../intercom-articles';

type SubscriptionModalProps = {
  isOpen: boolean;
  onDidDismiss: () => void;
};

const SubscriptionModal: React.FC<SubscriptionModalProps> = ({ isOpen, onDidDismiss }) => {
  const intl = useIntl();
  const { locale } = useLangs();
  const history = useHistory();

  const modalRef = useRef<HTMLIonModalElement>(null);

  const lastSubscription = useSelector(lastActiveSubscriptionSelector());
  const globalVars = useSelector(globalVariables);

  const { period_end, period_start, session_count, used_balance, therapist_id, id } =
    (lastSubscription as ISubscription) ?? {};

  const allDays = useMemo(() => {
    if (!period_end || !period_end) return 0;

    const start = DateTime.fromSeconds(period_start);
    const end = DateTime.fromSeconds(period_end);

    const diffDays = end.diff(start, 'days').toObject().days ?? 0;

    return Math.ceil(diffDays);
  }, [period_start, period_end]);

  const daysLeft = useMemo(() => {
    if (!period_end) return 0;

    const end = DateTime.fromSeconds(period_end);
    const toDay = DateTime.now();

    const diffDays = end.diff(toDay, 'days').toObject().days ?? 0;

    return Math.ceil(diffDays);
  }, [period_end]);

  if (!subscriptions) return null;

  return (
    <SubscriptionModalStyled ref={modalRef} isOpen={isOpen} onDidDismiss={onDidDismiss}>
      <PageHeader showAvatar={false} intercomId={SUBSCRIPTION_NOT_DO_IN_TIME} />
      <IonContent className={'blue'}>
        <Container style={{ justifyContent: 'flex-end' }}>
          <SubscriptionModalContainer>
            <InfoIcon className={'info-icon'} />
            <div className={'subscription-info'}>
              <FormattedMessage id={'subscription.left.title'} defaultMessage={'У вас залишилося'} />{' '}
              <b>
                {daysLeft}{' '}
                {decOfNum(daysLeft, [
                  intl.formatMessage({
                    id: 'other.days.one',
                    defaultMessage: 'день',
                  }),
                  intl.formatMessage({
                    id: 'other.days.many',
                    defaultMessage: 'дня',
                  }),
                  intl.formatMessage({
                    id: 'other.days.several',
                    defaultMessage: 'днів',
                  }),
                ])}
              </b>
              <br /> <FormattedMessage id={'subscription.will.booking.text'} defaultMessage={'щоб забронювати'} />{' '}
              <b>
                {session_count - used_balance}{' '}
                {decOfNum(session_count - used_balance, [
                  intl.formatMessage({
                    id: 'other.sessions.one',
                    defaultMessage: 'сеанс',
                  }),
                  intl.formatMessage({
                    id: 'other.sessions.many',
                    defaultMessage: 'сеанси',
                  }),
                  intl.formatMessage({
                    id: 'other.sessions.several',
                    defaultMessage: 'сеансів',
                  }),
                ])}
              </b>
            </div>
            <div className={'subscription-date-wrapper'}>
              <div className={'subscription-date'}>
                <FormattedMessage id={'general.plan.available.to'} defaultMessage={'План дійсний до'} />:{' '}
                <b>
                  {new Date(+period_end * 1000).toLocaleString(locale, {
                    day: 'numeric',
                    month: 'long',
                  })}
                </b>
              </div>
              <IonProgressBar className={'subscription-date-progress'} value={daysLeft / allDays} />
            </div>
            <div className={'subscription-balance-wrapper'}>
              <div className={'subscription-balance'}>
                <FormattedMessage id={'subscriptions.balance'} defaultMessage={'Баланс'} />:{' '}
                <b>
                  {session_count - used_balance}{' '}
                  {decOfNum(session_count - used_balance, [
                    intl.formatMessage({
                      id: 'other.sessions.one',
                      defaultMessage: 'сеанс',
                    }),
                    intl.formatMessage({
                      id: 'other.sessions.many',
                      defaultMessage: 'сеанси',
                    }),
                    intl.formatMessage({
                      id: 'other.sessions.several',
                      defaultMessage: 'сеансів',
                    }),
                  ])}
                </b>
              </div>
              <div className={'balance-list'}>
                {new Array(session_count).fill('').map((i, index) => (
                  <div key={index} className={`balance-item ${index + 1 > used_balance ? 'filled' : ''}`} />
                ))}
              </div>
            </div>
            <HelpButton articleId={SUBSCRIPTION_NOT_DO_IN_TIME} className={'help-btn'}>
              <FormattedMessage id={'general.need.help.that'} defaultMessage={'Мені потрібна допомога з цим'} />
            </HelpButton>
            <div className={'modal-desc'}>
              <FormattedMessage
                id={'subscription.modal.description'}
                defaultMessage={
                  'Ви можете вибрати будь-які дні для проведення сеансів протягом наступних {weeks} тижнів, але ви повинні забронювати їх протягом наступних'
                }
                values={{ weeks: globalVars.numberOfWeeksForScheduleRendering }}
              />{' '}
              {daysLeft}{' '}
              {intl.formatMessage({
                id: 'other.days.several',
                defaultMessage: 'днів',
              })}
              .
            </div>
            <CustomButton
              buttonType={'primary'}
              className={'booking-btn'}
              onClick={() => {
                modalRef.current?.dismiss();
                history.push(
                  PrivateRoutes.SUBSCRIPTION_BOOKING.replace(':id', therapist_id).replace(':subscriptionID', id)
                );
              }}
            >
              <FormattedMessage id={'general.booking.btn'} defaultMessage={'Забронювати сеанс'} />
            </CustomButton>
            <CustomButton buttonType={'primary'} className={'cancel-btn'} onClick={() => modalRef.current?.dismiss()}>
              <FormattedMessage id={'general.not.now.btn'} defaultMessage={'Не зараз'} />
            </CustomButton>
          </SubscriptionModalContainer>
        </Container>
      </IonContent>
    </SubscriptionModalStyled>
  );
};

export default SubscriptionModal;
